import { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import Button from 'src/components/ui/Button/Button';
import PageHeader from 'src/components/ui/PageHeader/PageHeader';
import { addModal, removeModal } from 'src/features/globalSlice';
import { sendNewPassword } from 'src/store/actions/authActions';
import {
  getManufacturerAccountDetails,
  removeManufacturerAccount,
  setManufacturerAccountDetails,
} from 'src/store/actions/manufacturerAccountActions';
import { ModalType } from 'src/store/models/Modal';
import { AccountStatus } from 'src/store/models/ServiceAccount';
import { useLoadingSelector } from 'src/utils/useLoadingSelector';

import classes from './ManufacturerAccountDetails.module.scss';
import ManufacturerAccountDetailsForm, {
  ManufacturerAccountDetailsFormData,
} from './ManufacturerAccountDetailsForm/ManufacturerAccountDetailsForm';

const defaultValues: ManufacturerAccountDetailsFormData = {
  manufacturer: {
    baseCompany: {
      city: '',
      country: '',
      eMail: '',
      line1: '',
      phone: '',
      title: '',
      zip: '',
      line2: '',
      notes: '',
      website: '',
    },
    contactPerson: '',
    eMail: '',
    phone: '',
    serviceAccountLimit: 0,
    appNames: '',
    image: null,
  },
  enabled: AccountStatus.ACTIVATED,
};

export interface ManufacturerAccountDetailsProps {
  edit?: boolean;
}

const ManufacturerAccountDetails: FC<ManufacturerAccountDetailsProps> = ({
  edit = false,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const params = useParams();

  const methods = useForm<ManufacturerAccountDetailsFormData>({
    mode: 'onChange',
    defaultValues,
  });

  const loading = useLoadingSelector({
    actionType: getManufacturerAccountDetails.toString(),
  });
  const manufacturerAccountDetails = useAppSelector(
    (state) => state.manufacturerAccount.manufacturerAccountDetails
  );

  useEffect(() => {
    dispatch(setManufacturerAccountDetails(null));
    if (edit) {
      dispatch(
        getManufacturerAccountDetails({ manId: params?.manufacturerId })
      );
    }
    return () => {
      dispatch(setManufacturerAccountDetails(null));
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (manufacturerAccountDetails) {
      methods?.reset({
        ...manufacturerAccountDetails,
        manufacturer: {
          ...manufacturerAccountDetails?.manufacturer,
          appNames: manufacturerAccountDetails?.manufacturer?.appNames?.join(),
        },
        enabled: manufacturerAccountDetails.enabled
          ? AccountStatus.ACTIVATED
          : AccountStatus.DEACTIVATED,
      });
    }
    // eslint-disable-next-line
  }, [manufacturerAccountDetails]);

  const removeAccountHandler = () => {
    const account = methods?.getValues();
    dispatch(
      addModal({
        title: t('common:are_you_sure'),
        body: t('manufacturer_account:remove_manufacturer_details', {
          manufacturer: account?.manufacturer?.baseCompany?.title,
        }),
        modalType: ModalType.WARNING,
        loadingAction: removeManufacturerAccount.type,
        primaryButtonText: t('common:confirm'),
        secondaryButtonText: t('common:cancel'),
        primaryAction: () => {
          dispatch(removeManufacturerAccount(account));
        },
        secondaryAction: () => dispatch(removeModal()),
      })
    );
  };

  const changePassword = () => {
    dispatch(
      addModal({
        title: t('settings:change_password'),
        body: `${t('login:press_button_to_reset', {
          account: manufacturerAccountDetails?.manufacturer?.title,
        })} ${t('login:press_button_to_reset_manufacturer')}`,
        modalType: ModalType.SUCCESS,
        loadingAction: sendNewPassword.type,
        primaryButtonText: t('login:send_new_password'),
        secondaryButtonText: t('common:cancel'),
        primaryAction: () => {
          dispatch(
            sendNewPassword({
              userId: manufacturerAccountDetails?.id,
              title: manufacturerAccountDetails?.manufacturer?.title,
            })
          );
        },
        secondaryAction: () => dispatch(removeModal()),
      })
    );
  };

  return (
    <FormProvider {...methods}>
      <div className={classes.Container}>
        <PageHeader
          title={edit ? t('common:edit_account') : t('common:create_new')}
          showBackButton
          backConfirmationText={
            methods?.formState.isDirty && t('common:changes_not_saved')
          }
          endNode={
            <Button
              type="submit"
              form="manufacturer-account-form"
              disabled={
                !(methods.formState?.isValid && methods.formState?.isDirty) ||
                loading
              }
              className={classes.HeaderButton}
            >
              {edit ? t('common:save_changes') : t('common:create_account')}
            </Button>
          }
        />
        <ManufacturerAccountDetailsForm edit={edit} loading={loading} />
        {edit && !loading && (
          <footer>
            <Button onClick={changePassword} variant="outlined">
              {t('settings:change_password')}
            </Button>
            <Button onClick={removeAccountHandler} variant="outlined">
              {t('service_account:delete_account')}
            </Button>
          </footer>
        )}
      </div>
    </FormProvider>
  );
};

export default ManufacturerAccountDetails;
